import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { api, describeError, useOp } from '../../api.ts';
import { useRouter } from '../../router.tsx';
import { runWrite, useChanges } from '../../ui/changes.ts';
import { ErrorBox, Loading } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';
import type { ColorKey, ContrastResult, Guardrails, Palette, TenancyOverview, ThemeDocument, ThemeLayer, ThemeLevel } from './types.ts';

const COLOR_ROWS: { key: ColorKey; label: string }[] = [
  { key: 'bg', label: 'Page background' },
  { key: 'surface', label: 'Card background' },
  { key: 'surfaceAlt', label: 'Muted background' },
  { key: 'border', label: 'Borders' },
  { key: 'text', label: 'Text' },
  { key: 'ink', label: 'Headings' },
  { key: 'muted', label: 'Secondary text' },
  { key: 'primary', label: 'Primary (buttons, links)' },
  { key: 'onPrimary', label: 'Text on primary' },
  { key: 'accent', label: 'Discount tag' },
  { key: 'onAccent', label: 'Text on discount tag' },
  { key: 'highlight', label: 'Highlight (decorative)' },
  { key: 'success', label: 'Success' },
  { key: 'warning', label: 'Warning' },
  { key: 'focus', label: 'Focus ring' },
];

const LEVEL_LABELS: Record<ThemeLevel, string> = { organization: 'Organization', brand: 'Brand', site: 'Site' };

const FONT_STACKS: Record<string, string> = {
  Inter: "Inter, system-ui, sans-serif",
  'Bricolage Grotesque': "'Bricolage Grotesque', system-ui, sans-serif",
  Fredoka: "Fredoka, system-ui, sans-serif",
  Georgia: "Georgia, 'Times New Roman', serif",
  'System UI': 'system-ui, sans-serif',
};

/** Theme layers (ADR 0003): organization → brand → site. Each layer sets only what differs; checks run as you type. */
export function Themes() {
  const { query, setQuery } = useRouter();
  const overview = useOp<TenancyOverview>('admin.tenancy.overview');
  const layers = useMemo(() => {
    const out: { level: ThemeLevel; ref: string; name: string; depth: number }[] = [];
    for (const org of overview.data?.organizations ?? []) {
      out.push({ level: 'organization', ref: org.ref, name: org.name, depth: 0 });
      for (const brand of org.brands) {
        out.push({ level: 'brand', ref: brand.ref, name: brand.name, depth: 1 });
        for (const site of brand.sites) out.push({ level: 'site', ref: site.ref, name: site.name, depth: 2 });
      }
    }
    return out;
  }, [overview.data]);
  const level = (query.get('level') as ThemeLevel | null) ?? layers.find((l) => l.level === 'brand')?.level ?? null;
  const ref = query.get('ref') ?? layers.find((l) => l.level === 'brand')?.ref ?? null;

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>Themes</h1>
          <p className="muted">How sites look: colours, fonts, corners and spacing. A theme never changes what a page does (links, code reveal, page structure).</p>
        </div>
      </header>
      {overview.error ? <ErrorBox error={overview.error} onRetry={overview.reload} /> : null}
      <div className="theme-layout">
        <nav className="theme-layers" aria-label="Theme layers">
          {!overview.data ? <Loading /> : null}
          <ul>
            {layers.map((layer) => (
              <li key={layer.ref} style={{ paddingLeft: layer.depth * 12 }}>
                <button type="button" className={`row-button ${layer.ref === ref ? 'active' : ''}`} aria-current={layer.ref === ref ? 'true' : undefined} onClick={() => setQuery({ level: layer.level, ref: layer.ref })}>
                  <span>{layer.name}</span>
                  <small className="muted">{LEVEL_LABELS[layer.level]}</small>
                </button>
              </li>
            ))}
          </ul>
        </nav>
        {level && ref ? <ThemeEditor key={`${level}:${ref}`} level={level} themeRef={ref} /> : null}
      </div>
    </section>
  );
}

function clean(document: ThemeDocument): ThemeDocument {
  const out: ThemeDocument = {};
  const light = Object.fromEntries(Object.entries(document.color?.light ?? {}).filter(([, v]) => v));
  const dark = Object.fromEntries(Object.entries(document.color?.dark ?? {}).filter(([, v]) => v));
  if (Object.keys(light).length || Object.keys(dark).length) out.color = { ...(Object.keys(light).length ? { light } : {}), ...(Object.keys(dark).length ? { dark } : {}) };
  const font: ThemeDocument['font'] = {};
  if (document.font?.display?.family) font.display = { family: document.font.display.family };
  if (document.font?.text?.family) font.text = { family: document.font.text.family };
  const roles = Object.fromEntries(Object.entries(document.font?.roles ?? {}).filter(([, v]) => v));
  if (Object.keys(roles).length) font.roles = roles;
  if (Object.keys(font).length) out.font = font;
  const radius = Object.fromEntries(Object.entries(document.radius ?? {}).filter(([, v]) => v));
  if (Object.keys(radius).length) out.radius = radius;
  if (document.density) out.density = document.density;
  return out;
}

function ThemeEditor({ level, themeRef }: { level: ThemeLevel; themeRef: string }) {
  const toast = useToast();
  const changes = useChanges();
  const layer = useOp<ThemeLayer>('admin.themes.get', { level, ref: themeRef }, changes);
  const [draft, setDraft] = useState<ThemeDocument>({});
  const [check, setCheck] = useState<(Guardrails & { palettes: { light: Palette; dark: Palette } | null }) | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);

  useEffect(() => {
    if (layer.data) setDraft(layer.data.document);
  }, [layer.data]);

  const cleaned = useMemo(() => clean(draft), [draft]);
  const dirty = layer.data ? JSON.stringify(cleaned) !== JSON.stringify(clean(layer.data.document)) : false;

  useEffect(() => {
    if (!layer.data?.canEdit) return;
    const timer = window.setTimeout(() => {
      api
        .op<Guardrails & { palettes: { light: Palette; dark: Palette } | null }>('admin.themes.check', { level, ref: themeRef, document: cleaned })
        .then((result) => {
          setCheck(result);
          setCheckError(null);
        })
        .catch((error: unknown) => setCheckError(describeError(error)));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [cleaned, level, themeRef, layer.data?.canEdit]);

  if (layer.error) return <ErrorBox error={layer.error} onRetry={layer.reload} />;
  if (!layer.data) return <Loading />;
  const data = layer.data;
  const guardrails = check ?? data.guardrails;
  const palettes = check?.palettes ?? data.palettes;
  const inherited = data.inherited;
  const disabled = !data.canEdit;

  const setColor = (scheme: 'light' | 'dark', key: ColorKey, value: string) =>
    setDraft((d) => ({ ...d, color: { ...d.color, [scheme]: { ...d.color?.[scheme], [key]: value.toUpperCase() } } }));
  const clearColor = (scheme: 'light' | 'dark', key: ColorKey) =>
    setDraft((d) => {
      const next = { ...d.color?.[scheme] };
      delete next[key];
      return { ...d, color: { ...d.color, [scheme]: next } };
    });
  const failing = guardrails.contrast.filter((r) => !r.passes);
  const radius = { ...inherited.radius, ...cleaned.radius };
  const font = { display: cleaned.font?.display?.family ?? inherited.font?.display?.family, text: cleaned.font?.text?.family ?? inherited.font?.text?.family };

  const save = () => runWrite(toast, 'admin.themes.save', { level, ref: themeRef, document: cleaned }, `Theme of ${data.name} saved; sites update within a minute`);

  return (
    <div className="theme-editor">
      <header className="theme-head">
        <h2>
          {data.name} <small className="muted">{LEVEL_LABELS[level]} layer</small>
        </h2>
        <div className="form-actions">
          <span className={guardrails.ok ? 'badge badge-ok' : 'badge badge-warn'} role="status">
            {guardrails.ok ? 'Checks pass' : `${failing.length + guardrails.issues.length + guardrails.below.length} check(s) fail`}
          </span>
          {data.canEdit ? (
            <>
              <button type="button" className="btn btn-ghost" disabled={!dirty} onClick={() => setDraft(data.document)}>
                Undo changes
              </button>
              <button type="button" className="btn btn-primary" disabled={!dirty || !guardrails.ok} onClick={save}>
                Save theme
              </button>
            </>
          ) : (
            <span className="muted small">Read only</span>
          )}
        </div>
      </header>
      {data.dropped.length ? <p className="dev-note">Stored values the editor cannot keep (they go away on save): {data.dropped.join(', ')}</p> : null}
      {checkError ? <p className="error-box">{checkError}</p> : null}

      <div className="theme-grid">
        <div>
          <table className="table compact theme-colors" aria-label="Colours">
            <thead>
              <tr>
                <th scope="col">Colour</th>
                <th scope="col">Light</th>
                <th scope="col">Dark</th>
              </tr>
            </thead>
            <tbody>
              {COLOR_ROWS.map(({ key, label }) => (
                <tr key={key}>
                  <th scope="row">{label}</th>
                  {(['light', 'dark'] as const).map((scheme) => {
                    const own = draft.color?.[scheme]?.[key];
                    const shown = palettes[scheme][key];
                    return (
                      <td key={scheme}>
                        <span className="color-cell">
                          <input
                            type="color"
                            aria-label={`${label}, ${scheme}`}
                            value={(own ?? shown ?? '#000000').toLowerCase()}
                            disabled={disabled}
                            onChange={(e) => setColor(scheme, key, e.target.value)}
                          />
                          <input
                            className={`hex ${own ? '' : 'inherited'}`}
                            aria-label={`${label}, ${scheme}, hex`}
                            value={own ?? ''}
                            placeholder={shown}
                            disabled={disabled}
                            maxLength={7}
                            onChange={(e) => (e.target.value ? setColor(scheme, key, e.target.value) : clearColor(scheme, key))}
                          />
                          {own && !disabled ? (
                            <button type="button" className="btn btn-small btn-ghost" title="Use the inherited value" aria-label={`Inherit ${label}, ${scheme}`} onClick={() => clearColor(scheme, key)}>
                              ×
                            </button>
                          ) : null}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted small">Empty fields inherit (grey value). Without any dark colours the site stays light in dark mode.</p>

          <fieldset className="form-grid cols-4" disabled={disabled}>
            <legend className="span-4">Type, corners and spacing</legend>
            {(['display', 'text'] as const).map((role) => (
              <label key={role}>
                {role === 'display' ? 'Headings font' : 'Body font'}
                <select
                  value={draft.font?.[role]?.family ?? ''}
                  onChange={(e) => setDraft((d) => ({ ...d, font: { ...d.font, [role]: e.target.value ? { family: e.target.value } : undefined } }))}
                >
                  <option value="">Inherit ({inherited.font?.[role]?.family ?? 'platform'})</option>
                  {data.menu.fontFamilies.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </label>
            ))}
            {(['heading', 'figure', 'accent'] as const).map((role) => (
              <label key={role}>
                {role === 'heading' ? 'Headings use' : role === 'figure' ? 'Discount figures use' : 'Store names and codes use'}
                <select
                  value={draft.font?.roles?.[role] ?? ''}
                  onChange={(e) => setDraft((d) => ({ ...d, font: { ...d.font, roles: { ...d.font?.roles, [role]: (e.target.value || undefined) as 'display' | 'text' | undefined } } }))}
                >
                  <option value="">Inherit</option>
                  <option value="display">Headings font</option>
                  <option value="text">Body font</option>
                </select>
              </label>
            ))}
            {(['sm', 'md', 'lg'] as const).map((size) => (
              <label key={size}>
                Corner radius {size}
                <input
                  type="number"
                  min={0}
                  max={40}
                  placeholder={inherited.radius?.[size]?.replace('px', '') ?? ''}
                  value={draft.radius?.[size]?.replace('px', '') ?? ''}
                  onChange={(e) => setDraft((d) => ({ ...d, radius: { ...d.radius, [size]: e.target.value === '' ? undefined : `${e.target.value}px` } }))}
                />
              </label>
            ))}
            <label>
              Spacing
              <select value={draft.density ?? ''} onChange={(e) => setDraft((d) => ({ ...d, density: e.target.value || undefined }))}>
                <option value="">Inherit ({inherited.density ?? 'regular'})</option>
                {data.menu.densities.map((density) => (
                  <option key={density} value={density}>
                    {density}
                  </option>
                ))}
              </select>
            </label>
          </fieldset>
          <p className="muted small">
            Layout variants (header, coupon card, hero): {Object.keys(data.menu.variants).length ? Object.keys(data.menu.variants).join(', ') : 'none in the menu yet; every site uses the tested platform components.'} AI theme proposals will arrive here as
            changes for you to approve.
          </p>
        </div>

        <div className="theme-side">
          <div className="preview-pair">
            {(['light', 'dark'] as const).map((scheme) => (
              <SampleCard key={scheme} scheme={scheme} palette={palettes[scheme]} radius={radius} font={font} />
            ))}
          </div>
          <Checks guardrails={guardrails} />
        </div>
      </div>
    </div>
  );
}

function SampleCard({ scheme, palette, radius, font }: { scheme: 'light' | 'dark'; palette: Palette; radius: Partial<Record<string, string>>; font: { display?: string; text?: string } }) {
  const style = {
    '--p-bg': palette.bg,
    '--p-surface': palette.surface,
    '--p-border': palette.border,
    '--p-text': palette.text,
    '--p-ink': palette.ink,
    '--p-muted': palette.muted,
    '--p-primary': palette.primary,
    '--p-on-primary': palette.onPrimary,
    '--p-accent': palette.accent,
    '--p-on-accent': palette.onAccent,
    '--p-success': palette.success,
    '--p-radius': radius['md'] ?? '10px',
    '--p-radius-sm': radius['sm'] ?? '6px',
    '--p-display': FONT_STACKS[font.display ?? 'Bricolage Grotesque'],
    '--p-body': FONT_STACKS[font.text ?? 'Inter'],
  } as CSSProperties;
  return (
    <figure className="sample" style={style} aria-label={`Sample coupon card, ${scheme}`}>
      <figcaption>{scheme === 'light' ? 'Light' : 'Dark'}</figcaption>
      <div className="sample-card">
        <span className="sample-tag">20% off</span>
        <div className="sample-body">
          <strong className="sample-store">ASOS</strong>
          <span className="sample-title">20% off full-price orders over £50</span>
          <span className="sample-muted">Ends 30 Nov · new customers only</span>
          <span className="sample-proof">✓ 12 orders placed after using this code</span>
        </div>
        <span className="sample-button">Get code</span>
      </div>
      <a className="sample-link" href="#preview" onClick={(e) => e.preventDefault()}>
        How we check codes
      </a>
    </figure>
  );
}

const PAIR_LABEL = (r: ContrastResult) => `${r.text} on ${r.background}`;

function Checks({ guardrails }: { guardrails: Guardrails }) {
  const failing = guardrails.contrast.filter((r) => !r.passes);
  return (
    <section className="card checks" aria-label="Checks">
      <h3 className="section-title">Checks</h3>
      {guardrails.issues.map((issue) => (
        <p key={issue.path} className="field-error">
          {issue.path}: {issue.message}
        </p>
      ))}
      {failing.length === 0 && guardrails.issues.length === 0 ? <p className="text-ok">All text is readable (WCAG AA 4.5:1) in light and dark.</p> : null}
      {failing.length ? (
        <ul className="contrast-list">
          {failing.map((r) => (
            <li key={`${r.scheme}-${PAIR_LABEL(r)}`} className="text-warn">
              <span className="swatch" style={{ background: r.backgroundColor }} />
              <span className="swatch" style={{ background: r.textColor }} /> {r.scheme}: {PAIR_LABEL(r)} is {r.ratio}:1 (needs 4.5)
            </li>
          ))}
        </ul>
      ) : null}
      {guardrails.below.length ? (
        <div className="text-warn">
          <strong>Breaks a layer below:</strong>
          <ul>
            {guardrails.below.map((b) => (
              <li key={b.ref}>
                {b.name}: {b.failing.map((r) => `${r.scheme} ${PAIR_LABEL(r)} ${r.ratio}:1`).join(', ')}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <details>
        <summary className="small">All pairs ({guardrails.contrast.length})</summary>
        <table className="table compact">
          <tbody>
            {guardrails.contrast.map((r) => (
              <tr key={`${r.scheme}-${PAIR_LABEL(r)}`}>
                <td className="small">{r.scheme}</td>
                <td className="small">{PAIR_LABEL(r)}</td>
                <td className={`num small ${r.passes ? 'text-ok' : 'text-warn'}`}>{r.ratio}:1</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </section>
  );
}
